import apiClient from 'api/services/apiClient';
import { MESSAGE_COMMON } from 'constants/string/messages';
import { isApiResponseError, validateMessageResponse } from 'utils/helper';

/**
 * @name getListChildren
 * @description Get the list of children
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListChildrenForChat = async (query = '') => {
  const response = await apiClient.request('GET', `/admin/children/list-children/for-chat${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};
/**
 * @name getListChildren
 * @description Get the list of children
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListChildren = async (query) => {
  const response = await apiClient.request('GET', `/admin/children${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name assignTherapist
 * @description Assign a therapist to a child
 * @param {string} child_id
 * @param {string} queryString
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const assignTherapist = async (child_id, queryString) => {
  const response = await apiClient.request('PUT', `/admin/children/${child_id}/assign-therapist`, queryString);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getChildrenDetail
 * @description Get the detail of a child
 * @param {string} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getChildrenDetail = async (id) => {
  const response = await apiClient.request('GET', `/admin/children/${id}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getListVideo
 * @description Get the list of videos of a child
 * @param {string} child_id
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListVideo = async (child_id, query) => {
  const response = await apiClient.request('GET', `/admin/children/get-report/by-report-ids${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getListDatesHasVideo
 * @description Get the list of dates that have videos of a child
 * @param {string} child_id
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListDatesHasVideo = async (child_id, queryString) => {
  const response = await apiClient.request('GET', `/admin/children/${child_id}/list-dates-of-report${queryString}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name createEvaluation
 * @description Create an evaluation
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const createEvaluation = async (data) => {
  const response = await apiClient.request('POST', '/admin/children/create-evaluation', data);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name updateEvaluation
 * @description Update an evaluation
 * @param {string} id
 * @param {object} data
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const updateEvaluation = async (id, data) => {
  const response = await apiClient.request('PUT', `/admin/children/evaluation/${id}`, data);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name checkStatusLastEvaluation
 * @description Check the status of the last evaluation
 * @param {string} child_id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const checkStatusLastEvaluation = async (child_id) => {
  const response = await apiClient.request('GET', `/admin/children/check-status-last-evaluation/${child_id}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name listDatesHasEvaluation
 * @description List the dates that have evaluations of a child
 * @param {string} child_id
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const listDatesHasEvaluation = async (child_id, query) => {
  const response = await apiClient.request('GET', `/admin/children/${child_id}/list-dates-has-evaluation${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getEvaluationDetail
 * @description Get the detail of an evaluation
 * @param {string} id
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getEvaluationDetail = async (session_id) => {
  const response = await apiClient.request('GET', `/admin/children/get-evaluation/${session_id}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name listEvaluation
 * @description List the evaluations of a child
 * @param {string} child_id
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const listEvaluation = async (query) => {
  const response = await apiClient.request('GET', `/admin/children/evaluation/list${query}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getListReportByReportIds
 * @description Get the list of reports by report ids
 * @param {string} report_ids
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListReportByReportIds = async (queryString) => {
  const response = await apiClient.request('GET', `/admin/children/get-report/by-report-ids${queryString}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};

/**
 * @name getListReportForEvaluation
 * @description Get the list of reports for evaluation
 * @param {string} query
 * @response success - 200 OK
 * @response failure - 404 Not Found
 * @returns {object} response.data
 */
export const getListReportForEvaluation = async (queryString) => {
  const response = await apiClient.request('GET', `/admin/children/list-report/for-evaluation${queryString}`);
  if (!isApiResponseError(response)) {
    return response?.data;
  } else {
    throw validateMessageResponse(response.jp_message, 'string', MESSAGE_COMMON.FAILURE);
  }
};
